import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import createScheduler from '@/polyfills/create-scheduler';
import { useAppSelector } from '@/store/hooks';
import { getToken } from '@/store/slices/user/selectors';
import { MediaItem } from '@/types/greenroom/media.types';
import { Shot } from '@/types/greenroom/shots.types';
import getShotDuration from '@/utils/greenroom/get-shot-duration';
import useResourcePool from '@/utils/greenroom/use-resource-pool';
import { selectGreenroomMedia, selectGreenroomMeeting, selectGreenroomShots } from '@/store/slices/project/selectors';

type TUseLoadGreenroomShots = {
  sessionUuid?: string;
};

const useLoadGreenroomShots = (props?: TUseLoadGreenroomShots) => {
  const params = useParams();
  const sessionUuid = props?.sessionUuid || params?.sessionUuid;
  const meetingData = useAppSelector(selectGreenroomMeeting);
  const token = useAppSelector(getToken);
  const greenroomShots = useAppSelector(selectGreenroomShots);
  const greenroomMedia = useAppSelector(selectGreenroomMedia);

  const [media, setMedia] = useState<(MediaItem & { duration?: number })[] | null>(null);
  const [shots, setShots] = useState<Shot[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [requiresMeetingId, setRequiresMeetingId] = useState(false);

  // const [generatedContent, setGeneratedContent] = useState<GeneratedContent[] | null>(null);

  const fetchingRef = useRef(false);

  useEffect(() => {
    if (!sessionUuid || !token || fetchingRef.current) return;

    // fetchingRef.current = true;
    (async () => {
      try {
        if (!meetingData?.meeting) {
          setRequiresMeetingId(true);
          return;
        }

        // was previously a hook but this data is now needed across the entire page
        setMedia(greenroomMedia);
        setShots(greenroomShots);

        // if (_generatedContent.status === 'fulfilled' && Array.isArray(_generatedContent.value)) {
        // 	setGeneratedContent(_generatedContent.value || []);
        // } else {
        // 	setGeneratedContent([]);
        // }
      } catch (error) {
        console.error('Error fetching greenroom shots', error);
      } finally {
        fetchingRef.current = false;
        setLoading(false);
      }
    })();
  }, [sessionUuid, token, meetingData, greenroomMedia, greenroomShots]);

  const { updatedMedia } = useGetMediaDurations({ shots, media });
  useEffect(() => {
    if (!updatedMedia) return;
    setMedia(updatedMedia);
  }, [updatedMedia]);

  return { loading, media, shots, requiresMeetingId /* , generatedContent */ };
};

export default useLoadGreenroomShots;

type TUseGetDurations = {
  shots: Shot[] | null;
  media: (MediaItem & { duration?: number })[] | null;
};

const useGetMediaDurations = ({ shots, media }: TUseGetDurations) => {
  const resourcePool = useResourcePool();

  const [updatedMedia, setUpdatedMedia] = useState<(MediaItem & { duration?: number })[] | null>(null);

  const getTotalDuration = useCallback(
    async (mediaItem: MediaItem): Promise<MediaItem & { duration: number }> => {
      let duration = 0;

      if (!shots || !media || !resourcePool) {
        return { ...mediaItem, duration };
      }

      const shot = shots?.find((s) => {
        return s.shot_data.media === mediaItem.id || s.shot_data.shotFeeds.some((sf) => sf.media === mediaItem.id);
      });
      if (!shot) return { ...mediaItem, duration };
      try {
        const newDuration =
          shot.shot_data.autoDuration && shot.shot_data.shotFeeds[0].media
            ? await getShotDuration(media, mediaItem.id, resourcePool)
            : shot.shot_data.shotDuration;
        duration += newDuration;
      } catch (error) {
        console.error('Error getting shot duration', error);
      }

      return { ...mediaItem, duration };
    },
    // do not add resourcePool to dependencies or we will get an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shots, media]
  );

  const triggered = useRef(false);

  useEffect(() => {
    if (!media) return;
    if (!shots) return;
    if (triggered.current) return; // prevents infinite loop

    const taskScheduler = createScheduler();
    taskScheduler
      .schedule(
        async () => {
          const results = await Promise.all(media.map((m) => getTotalDuration(m)));
          setUpdatedMedia(results);
          const hasDuration = results.some((r) => r.duration);
          if (hasDuration) {
            triggered.current = true;
          }
        },
        { priority: 'background', delay: 0 }
      )
      .catch(console.error);

    // only worry about getTotalDuration changing or we get infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTotalDuration]);

  return { updatedMedia };
};
